/*
 * LIVE console PRESENTATION contract — local preview.
 *
 * This is NOT the wire contract. The backend contract for the shipped Shadow
 * page lives in src/types/firstResponder.ts and is deliberately not imported
 * here. A future, separately-approved mapper translates real API payloads
 * into these view-model types.
 *
 * Vocabulary policy: every closed key in this file mirrors the vocabulary the
 * backend already established (graphy FirstResponder::Vocabulary and the
 * candidate-pool stage keys). The frontend owns the human labels; the backend
 * sends facts and keys, never display sentences.
 */

export type LiveMode = 'live' | 'shadow' | 'off';

/*
 * WHICH RUNS THE NUMBERS COUNT (GRAVY-10816 walk, W5). Every windowed count on the console is scoped
 * to one persisted run mode, and the jobs list beside them is not: after Stop and Start Shadow the
 * list still shows the live job with its accepted offer while the tiles read 0. This mirrors
 * graphy's PilotConfig::REPORTING_RUN_MODE exactly: shadow reads shadow rows; live and off both read
 * live rows, because a stopped pilot's console is asked what the pilot DID. The captions say it.
 */
export type LiveRunScope = 'shadow' | 'live';
export const runScopeFor = (mode: LiveMode): LiveRunScope =>
  mode === 'shadow' ? 'shadow' : 'live';

// Mirrors the runner's states. `needs_attention` = FR could not safely
// complete the intended wave (an operator can investigate). `failed` = the
// evaluation/run itself failed (infrastructure) — a system problem, never a
// business decision, and never rendered as a bare word.
//
// `shadow_selected` added for GRAVY-10373 convergence (real-data adapter, Phase 2): the real
// backend's Shadow-mode terminal state — FR selected a top-3 shortlist and recorded what it
// WOULD offer, but created no real JobOffer/push (Runner::STATES' `shadow_selected`, distinct
// from `completed`, which only occurs when OfferAction actually ran). Mapping this onto
// `offers_sent` would falsely claim real offers were sent; `watching` would hide that a decision
// was made. Neither preview fixture ever constructs this value — it is exercised only by the real
// adapter — but it is part of the same closed union so no exhaustive switch can silently miss it.
export type LiveJobState =
  | 'watching'
  | 'waiting'
  | 'shadow_selected'
  | 'offers_sent'
  | 'filled'
  | 'needs_attention'
  | 'skipped'
  | 'failed';

export type LiveTone = 'blue' | 'amber' | 'green' | 'red' | 'plain';

// The results window has TWO honest headings and they name different facts, so the label says
// which one it is: `since enabled <date>` dates the counts from the enable instant, and is only
// legal while that instant falls inside the window they were aggregated over; `since <date>`
// dates them from the reporting window's own start (the wire's `results.since`) — the heading for
// a pilot that is off, or one enabled before the window opened.
//
// B3: NO MEMBER OF THIS UNION CARRIES A NUMBER OF DAYS, and that is the whole point of the type.
// 'last 30 days' used to be the pilot-off heading and stated a window length no payload ever sent,
// which is the same fabrication as dating the reporting window's start as an enable time.
// 'last 24 h' was the last one left — captioning the offers and filled TILES while the counts
// under them came from a thirty-day report — and it is gone for the same reason, from the type
// first so the literal cannot come back anywhere the type reaches. 'window unknown' is the
// heading when the wire substantiates neither instant — the same word the metric cells use for a
// count nobody measured.
//
// What remains: two labels derived from instants the wire actually sent, and two that name no
// period at all ('now' for a live cohort, 'unresolved' for a backlog) and so cannot lie about one.
export type LiveWindowLabel =
  | 'now'
  | 'unresolved'
  | 'window unknown'
  | `since ${string}`
  | `since enabled ${string}`;

// value is null when the server cannot substantiate the metric. null renders "Unknown";
// a backend-PROVEN zero renders 0. The two must never collapse into each other.
export type LiveHeadlineStat = {
  value: number | null;
  windowLabel: LiveWindowLabel;
};

// The two WINDOWED tiles carry the results panel's own window label (see toStats); the two that
// measure a population rather than a period carry the caption that names that population.
export type LiveHeadlineStats = {
  watchingNow: LiveHeadlineStat; // 'now'
  offersSent: LiveHeadlineStat; // the results window
  shiftsFilled: LiveHeadlineStat; // the results window
  needsAttention: LiveHeadlineStat; // 'unresolved'
};

export type LivePilotClient = { id: number; name: string };

// The editable pilot policy value object — the SINGLE source for every
// settings display string (derived, never duplicated as prose).
export type LivePilotPolicy = {
  clients: LivePilotClient[];
  // Starts at the earliest actual worker-notification (job_notification_tiers)
  // send recorded for the job — NOT posted_at/reopened_at. Gives the existing
  // notification/marketplace flow time before First Responder evaluates.
  // 0 = no additional wait; still a distinct setting from minimum lead time.
  waitBeforeEvaluationMinutes: number;
  // Measured backward from shift start. Server-defined safety floor: Admin may
  // raise it but never lower it below the floor.
  minLeadMinutes: number;
  maxOffersPerJob: number;
  distanceLimitMiles: number;
  treatmentPercent: number; // job-level experiment allocation
  // NOTE: ranking order is deliberately NOT a policy field. It is a reviewed
  // ranking-policy version, never an Admin setting — see
  // previewRankingPolicy.LOCKED_RANKING_ORDER.
  version: string; // e.g. 'fr-reliability_first-r1'
};

export type LiveSettingsDraft = {
  policy: LivePilotPolicy;
  status: 'draft';
  basedOnVersion: string;
};

export type LiveAuditRow = {
  at: string;
  who: string;
  setting: string;
  from: string | null;
  to: string | null;
  reason: string;
};

// Closed attention reason classes — backend vocabulary keys. A bare "failed"
// is unrepresentable: every class carries its own qualified label.
// Mirrors WIRE_ATTENTION_REASON_CLASSES. The `push_*`/`sms_*` members describe an invitation that
// existed and could not reach the worker; the last two describe an attempt that never became an
// invitation at all.
export type LiveAttentionReasonClass =
  | 'push_failed'
  | 'push_no_device'
  | 'sms_no_phone'
  | 'transport_error'
  | 'invalid_offer';

export type LiveAttentionItem = {
  jobId: string;
  reasonClass: LiveAttentionReasonClass;
  summary: string;
};

// GRAVY-10373 I2: `count` is the wire's own unresolved_count — the TRUE total, which may exceed
// `items.length` when there is more than one page (25 rows) of open attention rows. The two are
// reconciled in the UI via a "Showing N of M" note rather than by forcing count to equal
// items.length. Falls back to items.length only when the server cannot substantiate a total.
export type LiveAttention = {
  count: number;
  items: LiveAttentionItem[];
} | null;

export type LiveClientFilter = {
  key: string;
  label: string;
  watching: number | null; // null,null => the "All" chip (no counts shown)
  filled: number | null;
};

// Countdown is only constructible for floor-satisfying watches.
// Rendering CLAMPS at zero: > 0 -> "Offers in N min"; <= 0 -> "Due now".
// `purpose` picks the copy: 'offers' (default) counts down to a first look, 'look_again' to a
// waiting activation's return ("Looks again in N min" / "Ready to look again").
export type LiveNext =
  | {
      kind: 'countdown';
      secondsRemaining: number;
      purpose?: 'offers' | 'look_again';
    }
  | { kind: 'text'; text: string };

/**
 * ONE EARLIER ACTIVATION of a job that reopened (GRAVY-10816 follow-up) - camelCased, the same
 * five facts the wire's `WireJobHistoryEntry` carries. Never a `filled` flag (Correction 3): this
 * is the run's own true state, read truthfully, never borrowed from the job's current fill
 * status. `stateLabel`/`tone` reuse the primary row's own state vocabulary (STATE_META in
 * liveWireAdapter.ts) rather than inventing a second one; `anchorSourceLabel` is the one new
 * vocabulary this follow-up adds (ANCHOR_SOURCE_LABELS, below).
 */
export type LiveJobHistoryEntry = {
  runId: string;
  // Venue-local, formatted the same way every other recorded instant on this console is
  // (formatVenueTime) - never a raw UTC ISO string.
  anchorAt: string;
  anchorSourceLabel: string;
  stateLabel: string;
  tone: LiveTone;
  // Raw. No second label vocabulary was specified for this one - the component humanises it
  // inline (underscores to spaces) the same way an unrecognised key falls back elsewhere here.
  stateReason: string | null;
};

export type LiveJobRow = {
  // The SHIFT: the business job id.
  id: string;
  // The EVALUATION: the First Responder run this row describes. null for a still-watching row,
  // which has no run yet. A job looked at twice has two rows with one id and two runIds.
  runId?: string | null;
  title: string;
  client: string;
  state: LiveJobState;
  stateLabel: string;
  tone: LiveTone;
  next: LiveNext;
  // "Workers available" — EXACTLY pool_after_gates. null renders "—".
  workersAvailable: number | null;
  // GRAVY-10373 Task 7: false ONLY for a real 'watching' row with no persisted run yet (the wire's
  // `id: "watch-<job_id>"` convention — GET .../jobs/:id 404s for these). The fixture/preview path
  // always synthesizes a full detail even for its own 'watching' rows, so it stays true there.
  // JobsCard renders a non-clickable row (with an explanatory title) instead of ever opening a
  // broken or empty modal.
  clickable: boolean;
  // This job's earlier activations (GRAVY-10816 follow-up). Already present on this row - an
  // operator expanding it to see them costs zero additional network calls (Correction 5). Never
  // nil; a single-activation job, and every watching row, carries [].
  history: LiveJobHistoryEntry[];
};

// Frontend casing of the canonical Gravy wire pagination
// { total, page, per_page, total_pages }. The snake->camel mapping happens in
// the fixture adapter (later: the API adapter) and NOWHERE else.
export type LivePagination = {
  total: number;
  page: number;
  perPage: number;
  totalPages: number;
};

// A server-shaped page. Components consume pages; they never receive a full
// collection to slice.
export type LivePage<T> = { items: T[]; pagination: LivePagination };

export type LiveWorkerEligibility =
  | 'selected'
  | 'selectable'
  | 'unselectable_unknown';

// One worker row of the recorded after-gates snapshot. Typed FACTS — the
// display strings (mi, Unknown, —) are composed in the components.
export type LiveWorkerRow = {
  id: string;
  displayName: string; // fixtures only — never real names
  eligibility: LiveWorkerEligibility;
  tier: string | null; // Platinum/Gold/Standard/Monitored; null renders "Unknown"
  score: number | null; // raw Dependability score — EVIDENCE ONLY; null renders "Unknown"
  distanceMiles: number | null; // null renders "Unknown"
  favorite: boolean;
  shiftsWithClient: number | null; // null renders "Unknown"
  selectionRank: number | null; // 1..N when selected, else null
  why: string; // policy-derived "Why selected / Why not selected" evidence
};

// Exclusion vocabulary = the real candidate-pool stage keys persisted on each
// run (graphy HistoricalCandidatePool). `supported` says whether the gate was
// APPLIED to this job: true carries an integer count (0 is a real answer —
// "this gate excluded nobody"), false means the gate never ran here and count
// stays null. `tax_type_mismatch` is always false: it is applied before
// discovery and therefore never counted.
export type LiveExclusionKey =
  | 'distance'
  | 'overlap'
  | 'blocked'
  | 'not_suspended'
  | 'lta_elsewhere'
  | 'certificates'
  | 'already_offered'
  | 'background_check'
  | 'ssn_compliance'
  | 'tax_type_mismatch'
  | 'already_hired'
  | 'coordinates';

export type LiveExclusionGroup = {
  key: LiveExclusionKey;
  label: string;
  count: number | null;
  supported: boolean;
};

// Frontend-owned closed-key labels for the exclusion gates. Shared by the dev preview's
// derivation (derive.ts) and the real adapter (Live/liveWireAdapter.ts) so a fixture row and a
// live row in the same gate are never worded differently — the same reason the persisted-check
// labels below live here rather than inside either consumer.
export const EXCLUSION_LABELS: Record<LiveExclusionKey, string> = {
  distance: 'Too far away',
  overlap: 'Working another shift',
  blocked: 'Blocked by this client',
  not_suspended: 'Account suspended',
  lta_elsewhere: 'Committed to another client',
  certificates: 'Missing a required certificate',
  already_offered: 'Already invited',
  background_check: 'Background check not cleared',
  ssn_compliance: 'Identity check outstanding',
  tax_type_mismatch: 'Different tax type',
  already_hired: 'Already on this job',
  coordinates: 'No usable location',
};

// Typed persisted gate evidence. The backend sends facts (key, passed,
// observed value, recordedAt, runId); the frontend owns the closed-key label
// and composes the sentence. Missing observations render "Unknown"/"—",
// never a manufactured zero.
export type LiveCheckObserved = {
  kind: 'duration_min' | 'timestamp' | 'count' | 'none';
  value: number | string | null;
};

export type LivePersistedCheck = {
  key: string;
  passed: boolean;
  observed: LiveCheckObserved;
  recordedAt: string | null;
  runId: string;
};

// Frontend-owned closed-key labels for check evidence.
export const CHECK_LABELS: Record<string, string> = {
  single_open_slot: 'One open slot',
  tier_sent: 'Notification alert',
  unfilled: 'Still unfilled',
  // Not "No Ops offer exists": the backend reports a COUNT (how many fresh Ops/FR invites are
  // out), and passes once that count is under the wave target - 1 or 2 fresh invites is a normal
  // PASS (room to top up), not an absence. The old label put a green check next to the number 2
  // right beside a claim that no offer existed. See observedText() below for the count text.
  no_ops_offer: 'Ops/FR invites within target',
  lead_time: 'At least 90 min before shift',
};

// Frontend-owned closed-key labels for a history entry's anchor source (GRAVY-10816 follow-up),
// same convention as EXCLUSION_LABELS/CHECK_LABELS above: the wire carries the backend's machine
// key (FirstResponder::Anchor::SOURCES), the frontend owns the words. An unrecognised source is
// humanised, never shown as its raw key (see anchorSourceLabelFor in liveWireAdapter.ts).
export const ANCHOR_SOURCE_LABELS: Record<string, string> = {
  publication: 'Shift posted',
  deficit_increase: 'A worker left the shift',
  decline: 'A worker declined an invite',
};

export type LiveFunnel = {
  poolSize: number | null;
  passedGates: number | null;
  selectedCount: number;
  // Offers actually created. Present on a live treatment run, where the send step can walk past
  // refused workers, so the funnel reads "tried → invited"; null/absent keeps "N selected".
  invitedCount?: number | null;
} | null;

export type LiveSignal = { label: string; value: string };

export type LiveCandidateDecision =
  | 'offer'
  | 'will_offer'
  | 'accepted'
  | 'closed'
  | 'withdrawn';

export type LiveCandidate = {
  /** selection_rank, not gate_rank: this collection IS the recorded selected wave. */
  rank: number;
  displayName: string;
  tier: string | null;
  score: number | null;
  distanceMiles: number | null;
  /** null until action evidence exists. Renders 'No action recorded' with plain tone. */
  decision: LiveCandidateDecision | null;
  decisionLabel: string;
  decisionTone: LiveTone;
  signals: LiveSignal[];
};

export type LiveTimelineEntry = { at: string | null; label: string };

// Attention resolution is bookkeeping ONLY. Terminal for FR automation:
// no retry/replacement affordance is representable here.
//
// F11: the event names the WORKER it is about. Every attention class describes an invitation that
// could not reach one specific person ("no device on file", "no phone number"), so a row without
// the worker states a problem the operator cannot act on. workerLabel is the internal
// `Worker #<id>` label derived from workerId — no real name crosses the wire (see
// liveWireContract.ts WORKER IDENTITY), so the two fields are the id and the one label it is
// allowed to render as, never a name.
export type LiveAttentionEvent = {
  workerId: number;
  workerLabel: string;
  reasonClass: LiveAttentionReasonClass;
  reason: string;
  raisedAt: string;
  resolution: 'open' | 'acknowledged' | 'archived';
  resolvedAt: string | null;
};

export type LiveFunnelStage = 'discovered' | 'after_gates' | 'selected';

// Safe failure evidence (closed codes). Unknown backend detail renders the
// investigation label — never invented specifics or raw exception text.
export type LiveFailureEvidence = {
  errorCode: 'evaluation_error';
  label: string; // SAFE_FAILURE_LABEL
  runId: string;
};

// The ONE sentence for a failed evaluation, for every closed error code. It lives here rather
// than inside either consumer for the same reason EXCLUSION_LABELS and CHECK_LABELS do: the dev
// preview's derivation (derive.ts) and the real adapter (Live/liveWireAdapter.ts) must word the
// same failure identically, and a second copy is a second thing to drift.
//
// Deliberately vague about the CAUSE: the wire carries a closed error code and a run id, never
// exception text, so the only honest thing to say is that the run failed and a human has to look.
// Inventing specifics here would put a guess in front of an operator as if it were evidence.
export const SAFE_FAILURE_LABEL = 'Evaluation failed — investigation required';

// Every detail is a RECORDED run: immutable history identified by run id,
// evaluated timestamp and the policy version persisted AT RUN TIME. Activating
// a new draft policy never re-ranks or rewrites any recorded run.
export type LiveRunMeta = {
  runId: string;
  evaluatedAt: string;
  policyVersion: string;
};

export type LiveJobDetail = {
  id: string;
  jobId: string; // business job identifier, e.g. '118432'
  title: string;
  client: string;
  // Nullable for GRAVY-10373 convergence (real-data adapter, Phase 2): the real legacy job-detail
  // API has no shift-start field at all (a known, already-documented gap — see gravy-docs
  // HANDOFF.md's "missing prototype fields are not fabricated" note). null renders "Unknown",
  // exactly like actsAt already does below — never a fabricated date.
  shiftStartsAt: string | null;
  actsAt: string | null;
  state: LiveJobState;
  stateLabel: string;
  tone: LiveTone;
  next: LiveNext;
  runMeta: LiveRunMeta;
  checks: LivePersistedCheck[];
  funnel: LiveFunnel;
  funnelStage: LiveFunnelStage | null;
  candidates: LiveCandidate[]; // the recorded wave, in selection order
  selectedWorkerIds: string[]; // persisted selection — golden-tested vs policy
  workersAvailableTotal: number | null; // pool_after_gates
  exclusions: LiveExclusionGroup[];
  timeline: LiveTimelineEntry[];
  attentionEvents: LiveAttentionEvent[];
  failure: LiveFailureEvidence | null;
  // Fix round 1 (Task 7 review, FINDING 3): non-null exactly when candidates.length !==
  // funnel.selectedCount — a persisted-data inconsistency the modal must surface loudly rather
  // than silently rendering a candidate list that disagrees with the run's own recorded count.
  // Fail loud, never blank the page: the recorded candidate rows still render underneath.
  integrityWarning: string | null;
};

// Every metric is nullable for the same reason as LiveHeadlineStat: an unsupported metric must
// render Unknown, never a manufactured 0. metabaseUrl is nullable so the link can be withheld
// rather than pointing nowhere.
export type LiveResultsSummary = {
  windowLabel: LiveWindowLabel; // 'since enabled <date>' or the window's own 'since <date>'
  shiftsActedOn: number | null;
  filledByFirstResponder: number | null;
  offersSent: number | null;
  accepted: number | null;
  noShowsOrDrops: number | null;
  failedActions: number | null;
  headline: string;
  metabaseUrl: string | null;
};

export type LiveSystemAlert = {
  key:
    | 'scheduler_stale'
    | 'configuration_unknown'
    | 'invariant_breach'
    | 'scheduler_failing';
  label: string;
  detail: string;
};

// Missing evidence is preserved, never coerced. null renders "Unknown",
// NEVER 0. A backend-proven zero renders 0.
export type LiveDiagnosticValue = number | null;

// Mirrors WIRE_GUARD_REASONS. job_blocked/worker_blocked aggregate every Preconditions blocker
// that is not one of the named guards.
export type LiveGuardReason =
  | 'wave_budget_exhausted'
  | 'worker_offer_frequency'
  | 'hourly_ceiling'
  | 'trigger_stale'
  | 'quiet_hours'
  | 'job_blocked'
  | 'worker_blocked';

export type LiveGuardSkip = {
  reason: LiveGuardReason;
  count: LiveDiagnosticValue;
};

// Frontend-owned closed-key labels for the guard-skip line, same rule as every other label map
// here: the wire carries the machine key, the frontend owns the words.
export const GUARD_SKIP_LABELS: Record<LiveGuardReason, string> = {
  wave_budget_exhausted: 'wave budget',
  worker_offer_frequency: 'worker frequency',
  hourly_ceiling: 'hourly ceiling',
  trigger_stale: 'stale trigger',
  quiet_hours: 'quiet hours',
  job_blocked: 'job state',
  worker_blocked: 'worker state',
};

export type LiveDiagnostics = {
  offersCreated: LiveDiagnosticValue;
  pushSent: LiveDiagnosticValue;
  smsSent: LiveDiagnosticValue;
  observedModes: string[];
  systemAlerts: LiveSystemAlert[];
  // null = the mode had no offer attempts at all, so nothing is known about guard skips. An empty
  // list, or a list of zeros, is the PROVEN "none" — a different claim.
  guardSkips: LiveGuardSkip[] | null;
  note: string | null;
};

// Closed discriminated union — 'unknown' can NEVER collapse into 'clear' or
// 'breached'.
export type LiveSafetyStatus =
  | { state: 'clear'; basis: LiveSafetyClearBasis }
  | { state: 'unknown'; reason: string }
  | { state: 'breached'; reasons: string[] };

/** Mirrors WIRE_SAFETY_CLEAR_BASES — `clear` is never bare. */
export type LiveSafetyClearBasis =
  | 'effective_off'
  | 'shadow_zero_action_proof'
  | 'live_bounded_action_proof';

export type LiveStopCopy = {
  title: string;
  body: string;
  inflight: string;
  sub: string;
};

// Operator-facing explanation of the recorded ranking order. DERIVED from the
// policy constants (bucket edges are business thresholds and never live in
// component copy).
export type LiveRankingExplainer = {
  intro: string;
  steps: string[];
  scoreNote: string;
  unknownNote: string;
};

// NOTE: the pilot policy is NOT part of the derived vm — the preview entry
// owns settings state (active/draft/audit) and passes the active policy down,
// so an activation updates the policy strip and settings card while every
// RECORDED run keeps its own persisted policyVersion untouched.
export type LiveConsoleViewModel = {
  // GRAVY-10373 C1: requested (configured intent) and effective (what the guards actually
  // permitted) are carried separately — mirrors the wire's own WireModeState. What is ARMED
  // (the button set, Stop) is a function of requestedMode; the badge shows requestedMode and
  // notes when a gate has halted it (effectiveMode 'off' while requestedMode is not).
  requestedMode: LiveMode;
  effectiveMode: LiveMode;
  safety: LiveSafetyStatus;
  stats: LiveHeadlineStats;
  attention: LiveAttention;
  filters: LiveClientFilter[];
  ranking: LiveRankingExplainer;
  detailsById: Record<string, LiveJobDetail>;
  results: LiveResultsSummary;
  diagnostics: LiveDiagnostics;
  stopConfirm: LiveStopCopy;
  fixtureNote: string;
};

export type LiveConsoleData =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'unauthorized' }
  | { status: 'ready'; vm: LiveConsoleViewModel; jobsLoading: boolean };
